import { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Checkbox, EllipsisMenu, Input } from '@weareopenr/component-library';
import { IOptionGroupItem, IOptionGroupValues } from '../types';
import styles from '../CreateOptionGroupModal.module.scss';
import { Autocomplete, TextField } from '@mui/material';
import { Listbox } from '../../GlobalItemBundleCategory/GlobalItemBundleCategoryFields';
import { AutocompleteOptionWithRanges } from 'src/components/AutocompleteOptionWithRanges';
import { EntitySource } from '../../../constants';
import { Range } from '@weareopenr/types';
import type { IOption } from '@weareopenr/component-library';
import { useInheritPosId } from '../../../hooks/useInheritPosId';

export interface IModifiersProps {
  index: number;
  modifiers: IOptionGroupItem[];
  ellipsisMenuOptions: IOption[];
  useFormReturn: UseFormReturn<IOptionGroupValues>;
  dataScrollRef: (node?: Element | null | undefined) => void;
  handleDeleteModifier: () => void;
  setSearchTerm: any;
  groupContentNameSingular: string;
}

interface ISelectedModifierProps {
  name: string;
  value: string;
  ranges: Range[];
}

const OptionGroupOption = ({
  index,
  modifiers,
  ellipsisMenuOptions,
  useFormReturn,
  dataScrollRef,
  handleDeleteModifier,
  setSearchTerm,
  groupContentNameSingular,
}: IModifiersProps) => {
  const { control, watch, setValue, getValues, register, trigger } = useFormReturn;
  const watchModifier = watch(`modifiers.${index}`);

  const modifierOptions: { name: string; value: string; ranges: Range[] }[] = modifiers.map((modifier) => ({
    name: modifier.name,
    value: modifier.parentItemId || modifier.optionItemId,
    ranges: modifier.ranges,
  }));

  useEffect(() => {
    const selectedItem = modifiers.find((modifier) => modifier.optionItemId === watchModifier.parentItemId);
    if (selectedItem) {
      setValue(`modifiers.${index}.name`, selectedItem.name);
    }
  }, [watch(`modifiers.${index}.parentItemId`)]);

  const useDefaultPrice = watch(`modifiers.${index}.useDefaultPrice`);
  useEffect(() => {
    if (useDefaultPrice) {
      setValue(`modifiers.${index}.valueWithTax`, null);
    }
  }, [useDefaultPrice]);

  const { inheritPosId, posIdRegisterOptions } = useInheritPosId({
    index,
    formReturn: { watch, setValue, getValues, register, trigger },
  });

  const validateModifier = (modifier: IOptionGroupItem, { ranges, source }: IOptionGroupValues) => {
    if (source === EntitySource.LIBRARY || !modifier.ranges || modifier.ranges.length === 0 || ranges.length === 0) {
      return true;
    }
    return !(
      ranges &&
      ranges?.length > 0 &&
      modifier.ranges &&
      !modifier.ranges.map((range) => range.range_id).some((range) => ranges.includes(range))
    );
  };

  const validation = {
    validate: {
      exists: (v) => v.optionItemId !== '' || 'Please select a modifier',
      nonMatchingModifierRange: validateModifier,
    },
  };

  return (
    <div className={styles['modifier-content']}>
      <div className={styles['select-wrapper']}>
        <Controller
          name={`modifiers.${index}`}
          control={control}
          rules={validation}
          render={({ field: { name, value, onBlur, ref }, fieldState: { error } }) => (
            <Autocomplete
              onInputChange={() => setSearchTerm(undefined)}
              value={value as any}
              className={styles['autocomplete']}
              options={modifierOptions || []}
              getOptionLabel={(option: ISelectedModifierProps) => option.name}
              freeSolo
              ListboxComponent={Listbox}
              renderOption={(props, option) => (
                <AutocompleteOptionWithRanges
                  {...props}
                  key={option.value}
                  ref={dataScrollRef}
                  role="list-box"
                  option={option}
                />
              )}
              onChange={(_event: React.SyntheticEvent<Element, Event>, newValue: ISelectedModifierProps) => {
                if (newValue === null) {
                  handleDeleteModifier();
                } else {
                  setValue(
                    `modifiers.${index}`,
                    {
                      ...newValue,
                      parentItemId: newValue.value,
                      optionItemId: newValue.value,
                      valueWithTax: 0,
                      posId: null,
                      useDefaultPrice: false,
                      inheritPosId: true,
                    },
                    { shouldValidate: true },
                  );
                }
              }}
              onBlur={onBlur}
              renderInput={(params) => {
                return (
                  <TextField
                    ref={ref}
                    {...params}
                    label={groupContentNameSingular}
                    name={name}
                    error={Boolean(error)}
                    helperText={error?.message}
                    FormHelperTextProps={{ role: 'alert', 'aria-label': `alert-${name}` }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                );
              }}
            />
          )}
        />
        <div className={styles['inherit-pos-id-container']}>
          <Checkbox name={`modifiers.${index}.inheritPosId`} register={register} />
        </div>
        <Input
          type="text"
          label="Override POS ID"
          register={posIdRegisterOptions}
          disabled={inheritPosId}
          error={useFormReturn.formState.errors?.modifiers?.[index]?.posId?.message}
        />

        <div className={styles['ellipsis-menu-container']}>
          <EllipsisMenu options={ellipsisMenuOptions} variant="ghost" />
        </div>
      </div>
    </div>
  );
};

export default OptionGroupOption;
